import type { TradeBriefingStructureScope } from '../types';
import {
  buildTradeBriefingTimberDisplayRows,
  formatTradeBriefingBoardFeet,
  sumTradeBriefingBoardFeet,
} from './tradeBriefingScope';

const FOREST: [number, number, number] = [44, 62, 47];
const MUTED: [number, number, number] = [79, 66, 58];
const MARGIN = 15;

export function slugifyTradeBriefingFilename(value: string): string {
  return (value || 'timber')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'timber';
}

export function tradeBriefingTimberPdfFilename(input: {
  projectTitle: string;
  structureName?: string;
}): string {
  const project = slugifyTradeBriefingFilename(input.projectTitle);
  const structure = input.structureName
    ? `-${slugifyTradeBriefingFilename(input.structureName)}`
    : '';
  return `${project}${structure}-timber.pdf`;
}

type JsPdfDoc = import('jspdf').jsPDF & { lastAutoTable?: { finalY: number } };

export async function downloadTradeBriefingTimberPdf(input: {
  structures: TradeBriefingStructureScope[];
  projectTitle: string;
  companyName?: string;
  tradeLabel?: string;
  structureName?: string;
}): Promise<void> {
  const rows = buildTradeBriefingTimberDisplayRows(input.structures);
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' }) as JsPdfDoc;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFillColor(...FOREST);
  doc.rect(0, 0, pageWidth, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Timber sizes and lengths', MARGIN, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Leiper\'s Creek Timber Frames · Trade briefing', MARGIN, 20);

  let y = 38;
  doc.setTextColor(...FOREST);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(input.projectTitle || 'Project briefing', MARGIN, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const meta = [input.companyName, input.tradeLabel, input.structureName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' · ');
  if (meta) {
    doc.text(meta, MARGIN, y);
    y += 7;
  } else {
    y += 2;
  }

  if (!rows.length) {
    doc.setTextColor(...MUTED);
    doc.setFontSize(10);
    doc.text('No timber lines on this briefing.', MARGIN, y);
  } else {
    const groups = new Map<string, typeof rows>();
    for (const row of rows) {
      const key = row.structureName;
      const list = groups.get(key) ?? [];
      list.push(row);
      groups.set(key, list);
    }

    groups.forEach((groupRows, structureName) => {
      const qty = groupRows[0]?.structureQuantity;
      const heading =
        qty && qty > 1 ? `${structureName} ×${qty}` : structureName;
      if (y > pageHeight - 50) {
        doc.addPage();
        y = MARGIN;
      }
      doc.setTextColor(...FOREST);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(heading, MARGIN, y);
      y += 3;

      const groupStructures = input.structures.filter(item => item.name === structureName);
      const groupBoardFeet = sumTradeBriefingBoardFeet(
        groupStructures.length ? groupStructures : input.structures,
      );

      autoTable(doc, {
        startY: y,
        head: [['Piece', 'Size', 'Length', 'Qty', 'Species', 'Grade', 'Dryness']],
        body: groupRows.map(row => [
          row.name,
          row.size,
          row.length,
          String(row.quantity),
          row.species || '—',
          row.grade || '—',
          row.dryness || '—',
        ]),
        margin: { left: MARGIN, right: MARGIN, bottom: 16 },
        tableWidth: pageWidth - MARGIN * 2,
        showHead: 'everyPage',
        styles: {
          fontSize: 9,
          cellPadding: 2.2,
          font: 'helvetica',
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: FOREST,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: { fillColor: [247, 244, 238] },
        columnStyles: {
          1: { cellWidth: 18 },
          2: { cellWidth: 18 },
          3: { cellWidth: 14, halign: 'right' },
          4: { cellWidth: 28 },
          5: { cellWidth: 18 },
          6: { cellWidth: 26 },
        },
        didDrawPage: data => {
          doc.setFontSize(8);
          doc.setTextColor(140);
          doc.text(
            `Page ${data.pageNumber}  ·  Sizes and order lengths from the proposal`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' },
          );
        },
      });
      y = (doc.lastAutoTable?.finalY ?? y) + 7;
      if (groupBoardFeet > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...FOREST);
        doc.text(`Total: ${formatTradeBriefingBoardFeet(groupBoardFeet)}`, MARGIN, y);
        y += 10;
      } else {
        y += 4;
      }
    });

    const grandTotal = sumTradeBriefingBoardFeet(input.structures);
    if (groups.size > 1 && grandTotal > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...FOREST);
      doc.text(`All structures: ${formatTradeBriefingBoardFeet(grandTotal)}`, MARGIN, y);
    }
  }

  doc.save(
    tradeBriefingTimberPdfFilename({
      projectTitle: input.projectTitle,
      structureName: input.structureName,
    }),
  );
}
